import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveLulavAction } from '../../../actions/saveLulavAction';
import './CreateLulavsPage.scss';

interface LulavItem {
  id: number;
  kind: string;
  price: number | string;
  year?: string;
  active?: boolean;
}

const CreateLulavsPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [lulavs, setLulavs] = useState<LulavItem[]>([]);
  const [kind, setKind] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLulavs = () => {
    axios
      .get('/api/v1/admin/lulavs', headers)
      .then(({ data }) => {
        setLulavs(data);
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data && data.lulavs) {
            setLulavs(data.lulavs);
          }
        });
      });
  };

  useEffect(() => {
    fetchLulavs();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind.trim() || !price) return;

    setLoading(true);
    dispatch(
      saveLulavAction(kind, price, headers, () => {
        setKind('');
        setPrice('');
        setLoading(false);
        fetchLulavs();
      })
    );
  };

  const handleToggleActive = (id: number, newActive: boolean) => {
    setLulavs(prev => prev.map(item => (item.id === id ? { ...item, active: newActive } : item)));
    axios.patch(`/api/v1/admin/lulavs/${id}`, { active: newActive }, headers).catch(() => {
      setLulavs(prev => prev.map(item => (item.id === id ? { ...item, active: !newActive } : item)));
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Lulav?')) return;
    setLulavs(prev => prev.filter(item => item.id !== id));
    axios.delete(`/api/v1/admin/lulavs/${id}`, headers).catch(() => {
      alert('Failed to delete Lulav');
      fetchLulavs();
    });
  };

  return (
    <div className="container admin-item-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage & Create Lulavim</h3>

        <div className="form-section">
          <h5 className="mb-3">Add New Lulav</h5>
          <form onSubmit={handleSubmit} className="inline-form">
            <div style={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Kind"
                value={kind}
                onChange={e => setKind(e.target.value)}
                required
              />
            </div>
            <div style={{ width: '120px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Price ($)"
                value={price}
                onChange={e => setPrice(e.target.value.replace(/[^0-9]/, ''))}
                required
              />
            </div>
            <div className="submit-btn-container">
              <button
                type="submit"
                className="btn btn-success"
                disabled={!kind.trim() || !price || loading}>
                {loading ? 'Saving...' : 'Save Lulav'}
              </button>
            </div>
          </form>
        </div>

        <h5>Current Lulavim ({lulavs.length})</h5>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>Price</th>
              <th>Year</th>
              <th>Active</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {lulavs.map(lulav => (
              <tr key={`lulav-${lulav.id}`}>
                <td>{lulav.id}</td>
                <td>{lulav.kind}</td>
                <td>${lulav.price}</td>
                <td>{lulav.year || new Date().getFullYear()}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={lulav.active ?? true}
                    onChange={e => handleToggleActive(lulav.id, e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <i
                    className="fa delete-icon"
                    title="Delete Lulav"
                    onClick={() => handleDelete(lulav.id)}>
                    &#xf014;
                  </i>
                </td>
              </tr>
            ))}
            {lulavs.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No Lulavim found for current year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateLulavsPage;
